import { useEffect, useReducer, useRef } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure } from "../../../telemetry";
import { fetchDesignations } from "../services/people";

/** Designations already looked up this session (null: the person has none, or isn't in the directory). */
const cache = new Map<string, string | null>();
/**
 * Set once Graph refuses the lookup (no permission to read job titles), so it
 * isn't asked again on every meeting. A permission granted later takes effect
 * after the add-in reloads.
 */
let unavailable = false;

const refusal = (e: unknown) => {
  const status = typeof e === "object" && e !== null ? (e as { status?: unknown }).status : undefined;
  return status === 400 || status === 403;
};

/**
 * Attendees' designations for the attendee list, keyed by email. Runs as a
 * runtime check: where Graph returns a job title or department it is shown,
 * and everyone else keeps showing their email address. Empty until loaded.
 */
export function useDesignations(token: string | null, emails: readonly string[]): ReadonlyMap<string, string | null> {
  const { refreshGraphToken } = useAuthState();
  // The designations live in the module cache; this only re-renders once a lookup has landed.
  const [, loaded] = useReducer((n: number) => n + 1, 0);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const key = [...new Set(emails)].sort((a, b) => a.localeCompare(b)).join(",");

  useEffect(() => {
    const current = tokenRef.current;
    if (!current || unavailable || !key) return;
    const missing = key.split(",").filter((e) => !cache.has(e));
    if (missing.length === 0) return;
    let cancelled = false;
    fetchDesignations(current, () => refreshGraphToken(false), missing)
      .then((found) => {
        found.forEach((designation, email) => cache.set(email, designation));
        if (!cancelled) loaded();
      })
      .catch((e: unknown) => {
        if (refusal(e)) unavailable = true;
        logConnectorFailure("calendar", e, "load-designations");
      });
    return () => {
      cancelled = true;
    };
  }, [key, refreshGraphToken]);

  return new Map(key ? key.split(",").map((e) => [e, cache.get(e) ?? null]) : []);
}
